import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LockKey, User } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import GoogleButton from "../components/GoogleButton";

export default function ParentLogin() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotUsername, setForgotUsername] = useState("");
    const [studentName, setStudentName] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotResult, setForgotResult] = useState(null);
    const [forgotError, setForgotError] = useState("");

    if (user) return <Navigate to={["admin", "finance", "coach"].includes(user.role) ? "/admin" : "/portal"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const data = await login(username, password);
            navigate(data.role === "admin" ? "/admin" : "/portal");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        setForgotError("");
        setForgotResult(null);
        setForgotLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", {
                username: forgotUsername,
                student_name: studentName,
            });
            setForgotResult(data);
        } catch (err) {
            setForgotError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FBF9F5] text-neutral-900" data-testid="parent-login-page">
            <div className="max-w-6xl mx-auto px-6 py-8">
                <Link to="/" data-testid="parent-login-back-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors">
                    <ArrowLeft size={16} /> Kembali ke Beranda
                </Link>

                <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-20 items-center min-h-[calc(100vh-120px)]">
                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="pt-12 lg:pt-0"
                    >
                        <p className="text-[11px] uppercase tracking-[0.35em] text-racing-gold mb-5">Portal Orang Tua</p>
                        <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-neutral-950">
                            Data anak.<br />
                            <span className="text-racing-red">Aman</span> & privat.
                        </h1>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-racing-muted leading-relaxed">
                            Masuk menggunakan username dan password akun Portal Orang Tua Anda. Akun dibuat saat pendaftaran atau diaktifkan oleh admin klub.
                        </p>
                        <div className="mt-10 grid sm:grid-cols-2 gap-px bg-racing-line border border-racing-line max-w-xl">
                            <div className="bg-white p-5">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Username</p>
                                <p className="text-sm text-neutral-900">Diberikan admin atau dibuat saat mendaftar</p>
                            </div>
                            <div className="bg-white p-5">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Belum Punya Akun?</p>
                                <p className="text-sm text-neutral-900" data-testid="login-no-account-note">Hubungi admin klub untuk aktivasi akun</p>
                            </div>
                        </div>
                    </motion.section>

                    <motion.section
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="bg-white border border-racing-line p-6 sm:p-8"
                    >
                        <div className="flex items-center gap-3 mb-8">
                            <BrandLogo className="w-10 h-10" testid="parent-login-logo" />
                            <div>
                                <p className="font-display uppercase tracking-wide leading-none">Mandalika Inlineskate</p>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-racing-muted mt-1">Login Orang Tua</p>
                            </div>
                        </div>

                        <form onSubmit={submit} className="space-y-5" data-testid="parent-login-form">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-racing-muted mb-2">Username</label>
                                <div className="relative">
                                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-racing-muted" />
                                    <input
                                        type="text"
                                        required
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        data-testid="parent-username-input"
                                        className="w-full bg-racing-surface border border-racing-line pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        placeholder="Username atau email"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-racing-muted mb-2">Password</label>
                                <div className="relative">
                                    <LockKey size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-racing-muted" />
                                    <input
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="parent-password-input"
                                        className="w-full bg-racing-surface border border-racing-line pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        placeholder="Password akun baru"
                                    />
                                </div>
                            </div>

                            {error && <p className="text-sm text-racing-red" data-testid="parent-login-error">{error}</p>}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="parent-login-submit"
                                className="w-full bg-racing-red hover:bg-racing-redhover disabled:opacity-50 text-white text-xs uppercase tracking-[0.25em] py-4 transition-colors duration-300"
                            >
                                {loading ? "Memproses..." : "Masuk Portal"}
                            </button>
                        </form>

                        <div className="mt-6 flex items-center gap-4" data-testid="parent-login-divider">
                            <span className="flex-1 h-px bg-racing-line" />
                            <span className="text-[10px] uppercase tracking-[0.25em] text-racing-muted">atau</span>
                            <span className="flex-1 h-px bg-racing-line" />
                        </div>
                        <div className="mt-5">
                            <GoogleButton context="login" label="Masuk dengan Google" testid="parent-google-login-button" />
                            <p className="mt-3 text-[11px] text-racing-muted leading-relaxed">
                                Khusus akun yang emailnya sudah terdaftar. Belum punya akun? Hubungi admin klub untuk aktivasi.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => setForgotOpen(!forgotOpen)}
                            data-testid="forgot-password-toggle"
                            className="mt-5 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors"
                        >
                            Lupa password?
                        </button>

                        {forgotOpen && (
                            <form onSubmit={submitForgot} className="mt-5 border-t border-racing-line pt-5 space-y-4" data-testid="forgot-password-form">
                                <p className="text-sm text-racing-muted">
                                    Permintaan reset akan dikirim ke <strong className="text-neutral-900">email admin klub</strong>. Admin akan menghubungi Anda setelah permintaan diproses.
                                </p>
                                <input
                                    type="text"
                                    required
                                    value={forgotUsername}
                                    onChange={(e) => setForgotUsername(e.target.value)}
                                    data-testid="forgot-username-input"
                                    className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                                    placeholder="Username akun orang tua"
                                />
                                <input
                                    type="text"
                                    value={studentName}
                                    onChange={(e) => setStudentName(e.target.value)}
                                    data-testid="forgot-student-input"
                                    className="w-full bg-racing-surface border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red"
                                    placeholder="Nama siswa (opsional)"
                                />
                                {forgotError && <p className="text-sm text-racing-red" data-testid="forgot-password-error">{forgotError}</p>}
                                {forgotResult && (
                                    <div className="bg-racing-surface border border-racing-line p-4" data-testid="forgot-password-success">
                                        <p className="text-sm text-neutral-900">{forgotResult.message}</p>
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    disabled={forgotLoading}
                                    data-testid="forgot-password-submit"
                                    className="w-full border border-racing-red text-racing-red hover:bg-racing-red hover:text-white disabled:opacity-50 text-xs uppercase tracking-[0.25em] py-3.5 transition-colors"
                                >
                                    {forgotLoading ? "Mengirim..." : "Buat Permintaan Reset"}
                                </button>
                            </form>
                        )}
                    </motion.section>
                </div>
            </div>
        </div>
    );
}
